import * as React from 'react'

import useReadAloudSettings from '../../hooks/readAloud/useReadAloudSettings'
import { setClipMissSession } from '../../libs/speech/clipMisses'
import { ReadAloudTool, startReadAloudAnalytics } from '../../libs/speech/readAloudAnalytics'

interface ReadAloudContextValue {
  readAloudEnabled: boolean;
}

interface ReadAloudProviderProps {
  activitySessionUid?: string | null;
  children: React.ReactNode;
  enabled?: boolean;
  tool?: ReadAloudTool;
}

interface ReadAloudValueProviderProps {
  children: React.ReactNode;
  enabled: boolean;
}

interface ResolvingReadAloudProviderProps {
  activitySessionUid?: string | null;
  children: React.ReactNode;
  tool?: ReadAloudTool;
}

const ReadAloudContext = React.createContext<ReadAloudContextValue>({ readAloudEnabled: false, })

const ReadAloudValueProvider = ({ children, enabled, }: ReadAloudValueProviderProps) => {
  const value = React.useMemo(() => ({ readAloudEnabled: enabled, }), [enabled])
  return <ReadAloudContext.Provider value={value}>{children}</ReadAloudContext.Provider>
}

const ResolvingReadAloudProvider = ({ activitySessionUid, children, tool, }: ResolvingReadAloudProviderProps) => {
  const { enabled, } = useReadAloudSettings({ activitySessionUid, })

  React.useEffect(() => { setClipMissSession(enabled ? activitySessionUid : null) }, [activitySessionUid, enabled])

  React.useEffect(() => {
    if (!enabled || !tool) { return undefined }

    return startReadAloudAnalytics({ activitySessionUid, tool, })
  }, [activitySessionUid, enabled, tool])

  return <ReadAloudValueProvider enabled={enabled}>{children}</ReadAloudValueProvider>
}

const ReadAloudProvider = ({ activitySessionUid, children, enabled, tool, }: ReadAloudProviderProps) => {
  if (enabled === undefined) {
    return <ResolvingReadAloudProvider activitySessionUid={activitySessionUid} tool={tool}>{children}</ResolvingReadAloudProvider>
  }
  return <ReadAloudValueProvider enabled={enabled}>{children}</ReadAloudValueProvider>
}

const useReadAloud = () => React.useContext(ReadAloudContext)

export { ReadAloudProvider, useReadAloud }
